/**
 * Kawaii food stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const kawaiiFoodStickersPack: ToilePack = {
    slug: 'kawaii-food-stickers',
    category: 'sticker',
    name: 'Kawaii food stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'boba', label: 'Boba tea', subject: 'a cup of boba tea with a straw and a tiny smiling face, pearls at the bottom'},
        {key: 'sushi', label: 'Sushi', subject: 'a sushi roll with a blushing face and a piece of salmon nigiri beside it'},
        {key: 'toast', label: 'Toast', subject: 'a slice of toast with a pat of butter and a sleepy smiling face'},
    ],
    styles: [
        {key: 'kawaii', label: 'Kawaii', style: 'kawaii sticker illustration, rounded shapes, tiny smiling faces, soft thick outlines', palette: 'Pastel pink, mint, lilac and cream with dark outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'doodle', label: 'Hand-drawn', style: 'hand-drawn marker doodle sticker, loose line, flat wash fills', palette: 'Two or three muted colours with a black line', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['a drink instead', 'for a bento box', 'as a dessert version'],
    directionHint: 'try: a drink instead',
    seo: {
        title: 'Kawaii Food Stickers: Boba, Sushi, Toast',
        metaDescription: 'Nine kawaii food stickers — boba tea, sushi, and toast, each with a tiny smiling face — in kawaii, bold cartoon, and hand-drawn styles.',
        h1: 'Kawaii food stickers: boba tea, sushi, toast',
        tagline: 'Nine stickers, three round-eyed foods, three cute styles each.',
        intro: 'Food with a face, the way the kawaii sticker economy likes it: a cup of boba with pearls at the bottom, a sushi roll with a blush, a slice of toast about to fall asleep. Each one rendered kawaii, bold cartoon, and loose hand-drawn, die-cut and ready to print. Ask for a drink instead, a bento box set, or a dessert version, and the same three styles carry it.',
        useCases: [
            'a water bottle or laptop lid',
            'a bullet journal or planner page',
            'a bento box or lunch bag',
            'a die-cut sticker sheet for a shop',
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine kawaii food stickers — a cup of boba tea, a sushi roll, and a slice of toast, each with a small smiling face — in kawaii, bold cartoon, and hand-drawn finishes.'},
            {q: 'Can I ask for different food, like a drink or a dessert?', a: 'Yes — the direction field takes any variation, including a drink instead, a bento box composition, or a dessert version.'},
            {q: 'What size should I print these at?', a: 'Each sticker is a square die-cut design, so it scales cleanly from a 2-inch vinyl sticker up to an A4 sticker sheet without redrawing.'},
            {q: 'Do the stickers come with the white die-cut border already on them?', a: 'Yes — each design is drawn with its own white border, ready for a home cutter or a print-and-cut shop order.'},
        ],
    },
}
